// Shared filter-pill styling for the Reports page, used by both the main
// filter bar (ReportsTable.jsx) and the category drill-down header
// (CategoryReportDetail.jsx). Extracted so both stay visually identical
// instead of two copies drifting apart over time.

import { taskTableOuterBorder } from "../../utils/taskTableGridSx";

// MUI's own "small" defaults differ between Button (~30px) and outlined
// TextField/Autocomplete (~40px) -- pinning both to the same explicit
// height/font is the only reliable way to make every pill match.
// Sized to match the app-wide small-button convention used on Inbox/AI
// Agents (plain MUI size="small" -- 0.8125rem/~34px, confirmed live on the
// AI Agents header buttons) -- flat, no per-breakpoint scaling, per
// font-sizing-scale.md's "controls never shrink further on mobile" rule.
// Previously a much larger responsive 44-46px/0.88-0.95rem (matched to the
// Tasks page's filter bar); Rahim asked (2026-09-04) for Reports to read at
// the same size as the rest of the app instead.
export const REPORTS_PILL_HEIGHT = "34px";
export const REPORTS_PILL_FONT_SIZE = "0.8125rem";

export const reportsFilterPillSx = {
  color: "text.secondary",
  borderColor: (theme) => theme.palette.divider,
  borderRadius: "8px",
  fontSize: REPORTS_PILL_FONT_SIZE,
  height: REPORTS_PILL_HEIGHT,
  padding: "0 18px",
  textTransform: "none",
  whiteSpace: "nowrap",
  backgroundColor: "background.paper",
  "& .MuiButton-startIcon": { color: "text.secondary" },
  // Border darkens on hover instead of filling grey -- matches the "All
  // members" dropdown's own default hover behavior (Rahim's explicit ask,
  // 2026-08-17: wanted these pills to match that one, not the plain grey
  // fill they had before).
  "&:hover": {
    borderColor: "text.primary",
    backgroundColor: "background.paper",
  },
};

// Shared dropdown-menu styling (Date Range / Select Category / All members /
// Customize columns menu on the Overview page, and the category drill-down's
// own member-filter popover) -- 16px radius, border+shadow, matches
// Dashboard's FilterDropdown numbers. Extracted 2026-08-17 so the
// drill-down popover (previously a bare unstyled MUI Popover) stops
// drifting from the Overview page's convention.
export const reportsDropdownPaperSx = {
  borderRadius: "16px",
  border: (theme) => `1px solid ${theme.palette.divider}`,
  boxShadow: (theme) => (theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.12)"),
  py: 0.5,
};

// Neutral grey selected-row background -- deliberately NOT action.selected /
// primary-tinted (Rahim, 2026-08-17: selection in these dropdowns should
// read as plain grey, never as the brand green accent).
export const reportsDropdownSelectedSx = {
  backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
};

// Shared TablePagination styling -- the Overview table's own finish
// (Tasks-inspired border-top divider, "1 of N" left-aligned instead of
// MUI's default right-aligned range label, flattened toolbar height).
// Extracted 2026-08-13 so the category drill-down table's own pagination
// (previously missing entirely) matches instead of inventing a second look.
export const reportsPaginationSx = {
  color: "text.secondary",
  fontSize: "14px",
  paddingBottom: { xs: "8px", sm: "6px", md: 0, lg: 0, xl: 0 },
  borderTop: (theme) => taskTableOuterBorder(theme),
  "& .MuiTablePagination-displayedRows": {
    color: "text.primary",
    fontSize: "14px",
    fontWeight: 500,
  },
  "& .MuiTablePagination-toolbar": {
    justifyContent: "flex-start",
    // Tightened (2026-08-17, Rahim's ask) -- freeing up a bit of the
    // footer's own footprint so the table area above it can be given that
    // same room back, fitting one more row before the leftover-space gap
    // shows up. Paired with ReportsTable.jsx's TableContainer height calc.
    minHeight: "20px",
    paddingLeft: { xs: "10px", sm: "12px", md: "14px", lg: "14px", xl: "16px" },
    paddingRight: { xs: "10px", sm: "12px", md: "14px", lg: "14px", xl: "16px" },
    paddingTop: "4px",
    paddingBottom: 0,
    gap: 1,
  },
  "& .MuiTablePagination-spacer": {
    flex: "0 0 0",
    display: "none",
  },
  "& .MuiIconButton-root": {
    color: "text.primary",
    padding: "4px",
    "& .MuiSvgIcon-root": {
      fontSize: "20px",
    },
  },
  "& .Mui-disabled": {
    color: "text.disabled",
  },
};

// Hides the browser's native number-input spin buttons (the up/down
// chevrons on a `type="number"` field). They render with an opaque white
// background baked in by the browser itself, ignoring the app's theme
// entirely -- shows up as a jarring white box around the whole input in
// dark mode, no CSS on the input can reach it (found 2026-08-18 on the
// duration value fields). Removing them outright is the only reliable
// fix rather than fighting UA-rendered controls -- values stay fully
// typeable, `inputProps={{min:0}}` still enforced on submit.
export const reportsNumberInputSx = {
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

// Shared "X of Y" page label (instead of MUI's default "1-10 of 23" range)
// -- easier to read at a glance, used by both tables' TablePagination.
export const reportsPaginationLabel = ({ page, count }, rowsPerPage) =>
  `${page + 1} of ${Math.max(1, Math.ceil(count / rowsPerPage))}`;

// Small dark-bubble hover tooltip for explaining what a column/category
// means -- same convention as Dashboard's ContactsPanel.jsx TAB_TOOLTIP_SX
// (2026-08-18: reused verbatim so hover-to-learn reads identically across
// the app instead of a second one-off tooltip style).
export const reportsInfoTooltipSx = {
  tooltip: {
    sx: {
      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
      color: (theme) => (theme.palette.mode === "dark" ? "#1A1A1A" : "#F5F5F5"),
      fontSize: "0.75rem",
      fontWeight: 500,
      borderRadius: "8px",
      maxWidth: 200,
      textAlign: "center",
      px: 1.25,
      py: 0.75,
      boxShadow: (theme) =>
        theme.palette.mode === "dark" ? "0 6px 16px rgba(0,0,0,0.35)" : "0 4px 12px rgba(0,0,0,0.15)",
    },
  },
  arrow: {
    sx: {
      color: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
    },
  },
};
